import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import * as nursery from "@/lib/nurseryEngine";
import type { BounceZone } from "@/lib/chartBounceZones";
import { createAssistantGreetingMessage } from "@/lib/chatGreeting";
import { inferBackendIdFromBaseUrl } from "@/lib/llmBackends";
import { fetchTokenMeta } from "@/lib/tokenMeta";
import type { ChatMessage, ChatSession, GitHubWorkspaceSettings, ModelSettings, UserAlgoPreset, StrategistThresholds } from "@/types";
import { Ctx } from "./AppCtx";
import type { 
  TradingMode, 
  ScalperUserConfig, 
  Theme, 
  ActivitySection, 
  SidebarMode, 
  PersistedBotTrade, 
  UserBounceZone, 
  ChartAnalyticsState, 
  MobileTab,
  BackendConfig,
  LayoutMode,
  SearchResult,
  AppToast,
  TwitterUser,
} from "./types";
import { setBackendConfig } from "@/lib/pumpPortalConfig";
import { githubPutFileContent } from "@/lib/githubApi";
import {
  isFileSystemAccessSupported,
  loadPersistedHandle,
  openLocalWorkspace,
  writeLocalFile,
  clearPersistedHandle,
} from "@/lib/localWorkspace";
import { SCALPER_PAPER_CONFIG } from "@/lib/scalperPaperConfig";
import { getSolUsd } from "@/lib/solUsd";
import { getBrowserLocale, type SupportedLocale } from "@/lib/i18n/locales";
import type { AirdropList, FlowGraph } from "@/lib/algo/graph";
import { looksLikeSolanaAddress } from "@/lib/solanaBalance";
const MODEL_STORAGE_KEY = "unt_model_settings_v1";
const GITHUB_WORKSPACE_KEY = "unt_github_workspace_v1";
const USER_ALGOS_KEY = "unt_user_algo_presets_v1";
const TRADING_MODE_STORAGE_KEY = "unt_trading_mode_v1";
const SELECTED_ALGO_KEY = "unt_selected_algo_v1";
const CA_MINT_KEY = "unt_ca_mint_v1";
const SCALPER_LIVE_BUY_SOL_KEY = "unt_scalper_live_buy_sol_v1";
const PERSISTED_TRADES_KEY = "unt_persisted_bot_trades_v1";
const PREFERRED_CURRENCY_KEY = "claw_preferred_currency";
const BOUNCE_ZONES_KEY = "unt_bounce_zones_v1";
const SCALPER_USER_CONFIG_KEY = "unt_scalper_user_config_v1";
const STRATEGIST_THRESHOLDS_KEY = "unt_strategist_thresholds_v1";
const FAVORITES_KEY = "unt_favorites_v1";
const HELIOS_KEY = "unt_helius_key_v1";
const LOCALE_STORAGE_KEY = "unt_locale";
const GLOBAL_AIRDROP_LISTS_KEY = "unt_global_airdrops_v1";
const PRESAVED_GRAPHS_KEY = "unt_presaved_graphs_v1";

function loadLocale(): SupportedLocale {
  try {
    const raw = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (raw) return raw as SupportedLocale;
    return getBrowserLocale();
  } catch {
    return "en";
  }
}

const SCALPER_LIVE_BUY_MIN = 0.001;
const SCALPER_LIVE_BUY_MAX = 25;
const MAX_STORED_MESSAGES = 120;
const MAX_SESSIONS = 10;
const MAX_PERSISTED_TRADES = 1000;

function loadTradingMode(): TradingMode {
  try {
    const raw = localStorage.getItem(TRADING_MODE_STORAGE_KEY);
    if (raw === "real" || raw === "paper") return raw;
    return "paper";
  } catch {
    return "paper";
  }
}

const envGoogleKey = (import.meta.env.VITE_GOOGLE_API_KEY as string | undefined)?.trim();

export const defaultModel: ModelSettings = envGoogleKey
  ? {
      providerLabel: "Google AI Studio",
      baseUrl: "https://generativelanguage.googleapis.com/v1beta/openai",
      apiKey: envGoogleKey,
      model: "gemini-3.6-flash",
      llmBackendId: "google-ai-studio",
    }
  : {
      providerLabel: "OpenAI",
      baseUrl: "https://api.openai.com/v1",
      apiKey: "",
      model: "gpt-4o-mini",
      llmBackendId: "openai",
    };

function loadModel(): ModelSettings {
  try {
    const raw = localStorage.getItem(MODEL_STORAGE_KEY);
    if (!raw) return defaultModel;
    const parsed = { ...defaultModel, ...JSON.parse(raw) } as ModelSettings;
    if (!parsed.llmBackendId) {
      const fromUrl = inferBackendIdFromBaseUrl(parsed.baseUrl);
      if (fromUrl) parsed.llmBackendId = fromUrl;
    }
    // Auto-migrate deprecated models
    if (parsed.model === "gemini-2.0-flash" || parsed.model === "google/gemini-2.0-flash-001") {
      parsed.model = parsed.model.includes("google/") ? "google/gemini-3.6-flash" : "gemini-3.6-flash";
    }
    return parsed;
  } catch {
    return defaultModel;
  }
}

function makeGreetingSession(id?: string, name?: string): ChatSession {
  return {
    id: id ?? crypto.randomUUID(),
    name: name ?? "Chat 1",
    messages: [createAssistantGreetingMessage()],
    createdAt: Date.now(),
  };
}


function loadUserAlgos(): UserAlgoPreset[] {
  try {
    const raw = localStorage.getItem(USER_ALGOS_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as UserAlgoPreset[];
  } catch {
    return [];
  }
}

const defaultGithubWorkspace: GitHubWorkspaceSettings = {
  token: "",
  owner: "",
  repo: "",
  branch: "main",
};

function loadSelectedAlgoId(): string | null {
  try {
    return localStorage.getItem(SELECTED_ALGO_KEY) ?? null;
  } catch {
    return null;
  }
}

const ROGUE_MINT_BLACKLIST = "dmxgg48pyynprxdbkxvtzs1kzojc6g6efme8jaoypump";

function loadCaMintInput(): string {
  try {
    const params = new URLSearchParams(window.location.search);
    let urlMint = params.get("mint") || params.get("ca") || params.get("token") || params.get("ticker");
    if (!urlMint) {
      const match = window.location.pathname.match(/^\/(?:token|ca|mint|app)\/([a-zA-Z0-9]{10,44})/i);
      if (match) urlMint = match[1];
    }
    if (urlMint) {
      if (urlMint.toLowerCase() === ROGUE_MINT_BLACKLIST) {
        // Purge rogue URL param cleanly
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete("mint");
          url.searchParams.delete("ca");
          url.searchParams.delete("token");
          url.searchParams.delete("ticker");
          window.history.replaceState({}, "", url);
        } catch {}
        localStorage.removeItem(CA_MINT_KEY);
        return "";
      }
      localStorage.setItem(CA_MINT_KEY, urlMint);
      return urlMint;
    }
    const stored = localStorage.getItem(CA_MINT_KEY) ?? "";
    if (stored.toLowerCase() === ROGUE_MINT_BLACKLIST) {
      localStorage.removeItem(CA_MINT_KEY);
      return "";
    }
    return stored;
  } catch {
    return "";
  }
}

function clampScalperLiveBuySol(n: number): number {
  if (!Number.isFinite(n)) return SCALPER_PAPER_CONFIG.realBuySol;
  return Math.min(SCALPER_LIVE_BUY_MAX, Math.max(SCALPER_LIVE_BUY_MIN, n));
}

function loadScalperLiveBuySol(): number {
  try {
    const raw = localStorage.getItem(SCALPER_LIVE_BUY_SOL_KEY);
    if (!raw) return SCALPER_PAPER_CONFIG.realBuySol;
    return clampScalperLiveBuySol(Number(raw));
  } catch {
    return SCALPER_PAPER_CONFIG.realBuySol;
  }
}

function loadGithubWorkspace(): GitHubWorkspaceSettings {
  try {
    const raw = localStorage.getItem(GITHUB_WORKSPACE_KEY);
    if (!raw) return { ...defaultGithubWorkspace };
    return { ...defaultGithubWorkspace, ...JSON.parse(raw) } as GitHubWorkspaceSettings;
  } catch {
    return { ...defaultGithubWorkspace };
  }
}

function defaultScalperUserConfig(): ScalperUserConfig {
  return {
    dipMinPct: SCALPER_PAPER_CONFIG.dipMinPct,
    catalystMinSol: SCALPER_PAPER_CONFIG.catalystMinSol,
    minRoc1m: SCALPER_PAPER_CONFIG.minRoc1m,
    minWhaleSol: SCALPER_PAPER_CONFIG.minWhaleSol,
    takeProfitPct: SCALPER_PAPER_CONFIG.takeProfitPct,
    minOrderBookSellSolForStop: SCALPER_PAPER_CONFIG.minOrderBookSellSolForStop,
    realSlippagePct: SCALPER_PAPER_CONFIG.realSlippagePct,
    realPriorityFeeSol: 0.001,
    reentryCooldownMs: 30_000, // 30 s default — prevents rapid double-entry on real money
    filterMaxAgeHours: 48,
    filterMinLiqUsd: 30000,
    filterMinBuys24h: 300,
    filterMaxTop10Pct: 40,
    megaFilterEnabled: false,
  };
}

function loadScalperUserConfig(): ScalperUserConfig {
  try {
    const raw = localStorage.getItem(SCALPER_USER_CONFIG_KEY);
    if (!raw) return defaultScalperUserConfig();
    return { ...defaultScalperUserConfig(), ...JSON.parse(raw) } as ScalperUserConfig;
  } catch {
    return defaultScalperUserConfig();
  }
}

const defaultStrategistThresholds: StrategistThresholds = {
  minConfidence: 70,
  minBonding: 92,
  minWhaleEntrySol: 0.5,
  minWhaleCount: 3,
};

function loadStrategistThresholds(): StrategistThresholds {
  try {
    const raw = localStorage.getItem(STRATEGIST_THRESHOLDS_KEY);
    if (!raw) return defaultStrategistThresholds;
    return { ...defaultStrategistThresholds, ...JSON.parse(raw) } as StrategistThresholds;
  } catch {
    return defaultStrategistThresholds;
  }
}

const THEME_STORAGE_KEY = "unt_theme_v1";

function loadTheme(): Theme {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    if (!raw) return "dark";
    let parsed = raw;
    try { parsed = JSON.parse(raw); } catch {}
    if (parsed === "light" || parsed === "dark") return parsed;
    if (raw === "light" || raw === "dark") return raw;
    return "dark";
  } catch {
    return "dark";
  }
}

const LAYOUT_MODE_KEY = "unt_layout_mode_v1";

function loadLayoutMode(): LayoutMode {
  try {
    const raw = localStorage.getItem(LAYOUT_MODE_KEY);
    if (raw === "standard" || raw === "lily") return raw;
  } catch {}
  return "standard";
}

const SEARCH_HISTORY_KEY = "unt_search_history_v1";

function loadSearchHistory(): SearchResult[] {
  try {
    const raw = localStorage.getItem(SEARCH_HISTORY_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return [];
}

function loadBounceZones(): UserBounceZone[] {
  try {
    const raw = localStorage.getItem(BOUNCE_ZONES_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as UserBounceZone[];
  } catch {
    return [];
  }
}

function loadGlobalAirdropLists(): AirdropList[] {
  try {
    const raw = localStorage.getItem(GLOBAL_AIRDROP_LISTS_KEY);
    if (raw) return JSON.parse(raw) as AirdropList[];
  } catch {}
  return [];
}

function loadPresavedGraphs(): FlowGraph[] {
  try {
    const raw = localStorage.getItem(PRESAVED_GRAPHS_KEY);
    if (raw) return JSON.parse(raw) as FlowGraph[];
  } catch {}
  return [];
}

function loadPersistedTrades(): PersistedBotTrade[] {
  try {
    const raw = localStorage.getItem(PERSISTED_TRADES_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as PersistedBotTrade[];
  } catch {
    return [];
  }
}

const defaultChartAnalytics: ChartAnalyticsState = {
  mint: null,
  chartLoading: false,
  chartError: null,
  yMcCap: null,
  chartInterval: null,
  lastCandle: null,
  tapeSummary: null,
  tokenSupplyUi: null,
  orderBookConn: "idle",
  orderBookError: null,
  orderBookLastTradeAt: null,
  paperScalper: null,
  realBotTrades: [],
  livePumpPortalLastSig: null,
  livePumpPortalLastErr: null,
  stats: null,
  visionDetectStatus: "idle",
  visionDetectError: null,
};

function lsSave(key: string, val: any) {
  try {
    localStorage.setItem(key, JSON.stringify(val));
  } catch {
    /* ignore */
  }
}

// (Context defined in AppCtx.ts)
export { useApp } from "./AppCtx";
export * from "./types";

export function AppProvider({ children }: { children: ReactNode }) {
  const [model, setModelState] = useState(loadModel);
  const [isDeployModalOpen, setIsDeployModalOpen] = useState(false);
  const [locale, setLocaleState] = useState<SupportedLocale>(loadLocale);
  const [searchHistory, setSearchHistory] = useState<SearchResult[]>(loadSearchHistory);
  const [preferredCurrency, setPreferredCurrencyState] = useState<string>(() => localStorage.getItem(PREFERRED_CURRENCY_KEY) ?? "USD");

  const addToSearchHistory = useCallback((item: SearchResult) => {
    setSearchHistory(prev => {
      // Remove if it already exists to put it at the top
      const filtered = prev.filter(p => p.mint !== item.mint);
      const next = [item, ...filtered].slice(0, 20); // Keep max 20 items
      lsSave(SEARCH_HISTORY_KEY, next);
      return next;
    });
  }, []);

  const removeFromSearchHistory = useCallback((mint: string) => {
    setSearchHistory(prev => {
      const next = prev.filter(p => p.mint !== mint);
      lsSave(SEARCH_HISTORY_KEY, next);
      return next;
    });
  }, []);

  const setLocale = useCallback((l: SupportedLocale) => {
    setLocaleState(l);
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, l);
    } catch (e) {
      console.error("Failed to save locale", e);
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  // Chat sessions intentionally NOT loaded from localStorage — always start fresh.
  const [sessionsState, setSessionsState] = useState(() => {
    const s = makeGreetingSession();
    return { sessions: [s], activeId: s.id };
  });
  const chatSessions = sessionsState.sessions;
  const activeChatId = sessionsState.activeId;

  const [launchProviderFilter, setLaunchProviderFilter] = useState<string[]>(["Pump.fun", "Moonshot", "Raydium", "Meteora"]);

  const [sidebarMode, setSidebarMode] = useState<SidebarMode>("nursery");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activitySection, setActivitySection] = useState<ActivitySection>("nursery");
  const [chartAnalytics, setChartAnalyticsState] = useState<ChartAnalyticsState>(defaultChartAnalytics);
  const [composerBusy, setComposerBusy] = useState(false);
  const [userAlgos, setUserAlgos] = useState(loadUserAlgos);
  const [selectedAlgoId, setSelectedAlgoIdState] = useState<string | null>(loadSelectedAlgoId);
  const [caMintInput, setCaMintInputState] = useState(loadCaMintInput);
  const [scalperLiveBuySol, setScalperLiveBuySolState] = useState(loadScalperLiveBuySol);
  const [tradingMode, setTradingModeState] = useState<TradingMode>(loadTradingMode);
  const [algoSessionActive, setAlgoSessionActive] = useState(true);
  const [tradingHalted, setTradingHalted] = useState(false);
  const [manualSellRequested, setManualSellRequested] = useState(false);
  const [scalperUserConfig, setScalperUserConfigState] = useState<ScalperUserConfig>(loadScalperUserConfig);
  const [strategistMode, setStrategistMode] = useState(false);
  const [autoPilotEnabled, setAutoPilotEnabled] = useState(false);
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(false);
  const [layoutMode, setLayoutModeState] = useState<LayoutMode>(loadLayoutMode);
  const [bounceZones, setBounceZones] = useState<UserBounceZone[]>(loadBounceZones);
  const [persistedBotTrades, setPersistedBotTrades] = useState<PersistedBotTrade[]>(loadPersistedTrades);
  const [githubWorkspace, setGithubWorkspaceState] = useState(loadGithubWorkspace);
  const [openFilePath, setOpenFilePath] = useState<string | null>(null);
  const [openFileContent, setOpenFileContent] = useState<string | null>(null);
  const [workspaceFilePaths, setWorkspaceFilePaths] = useState<string[]>([]);
  const [applyEditTick, setApplyEditTick] = useState(0);
  const [lastAppliedPath, setLastAppliedPath] = useState<string | null>(null);
  const [localWorkspaceHandle, setLocalWorkspaceHandleState] = useState<FileSystemDirectoryHandle | null>(null);
  const [pendingLocalEdits, setPendingLocalEdits] = useState<Map<string, string>>(new Map());
  const [theme, setThemeState] = useState<Theme>(loadTheme);
  const [strategistThresholds, setStrategistThresholdsState] = useState<StrategistThresholds>(loadStrategistThresholds);
  const [favorites, setFavorites] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(FAVORITES_KEY);
      if (raw) return JSON.parse(raw);
    } catch {}
    return [];
  });
  const [heliosKey, setHeliosKeyState] = useState<string>(() => {
    try {
      return localStorage.getItem(HELIOS_KEY) ?? "";
    } catch {
      return "";
    }
  });
  const [backendConfig, setBackendConfigState] = useState<BackendConfig | null>(null);
  const [isFocusChartVisible, setIsFocusChartVisible] = useState(true);
  const [isTrading, setIsTrading] = useState(false);
  const [solPrice, setSolPrice] = useState(150);
  const [globalAirdropLists, setGlobalAirdropListsState] = useState<AirdropList[]>(loadGlobalAirdropLists);
  const [presavedGraphs, setPresavedGraphsState] = useState<FlowGraph[]>(loadPresavedGraphs);
  const [twitterUser, setTwitterUser] = useState<TwitterUser | null>(null);

  useEffect(() => {
    fetch("/api/auth/me")
      .then((res) => {
        if (res.ok) return res.json();
        throw new Error(`HTTP ${res.status}`);
      })
      .then((data) => {
        if (data.user) {
          setTwitterUser(data.user);
        }
      })
      .catch((err) => console.warn("[AppContext] Failed to load Twitter user:", err));
  }, []);

  const loginWithTwitter = useCallback(() => {
    window.location.href = "/api/auth/twitter";
  }, []);

  const logoutTwitter = useCallback(async () => {
    try {
      await fetch("/api/auth/logout", { method: "POST" });
      setTwitterUser(null);
    } catch (e) {
      console.error("[AppContext] Logout failed", e);
    }
  }, []);
  const [appToasts, setAppToasts] = useState<AppToast[]>([]);
  
  const addAppToast = useCallback((message: string, type: "info" | "success" | "error" | "neutral" = "info") => {
    const id = crypto.randomUUID();
    setAppToasts((prev) => [...prev, { id, message, type }]);
    // Auto dismiss after 3 seconds
    setTimeout(() => {
      setAppToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3000);
  }, []);

  const removeAppToast = useCallback((id: string) => {
    setAppToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const setGlobalAirdropLists = useCallback((lists: AirdropList[]) => {
    setGlobalAirdropListsState(lists);
    lsSave(GLOBAL_AIRDROP_LISTS_KEY, lists);
  }, []);

  const setPresavedGraphs = useCallback((graphs: FlowGraph[]) => {
    setPresavedGraphsState(graphs);
    lsSave(PRESAVED_GRAPHS_KEY, graphs);
  }, []);

  const savePresavedGraph = useCallback((graph: FlowGraph) => {
    setPresavedGraphsState((prev) => {
      // Find and update if exists, otherwise prepend
      const existing = prev.findIndex((g) => g.name === graph.name);
      let next;
      if (existing >= 0) {
        next = [...prev];
        next[existing] = graph;
      } else {
        next = [graph, ...prev];
      }
      lsSave(PRESAVED_GRAPHS_KEY, next);
      return next;
    });
  }, []);

  const [mobileActiveTab, setMobileActiveTabState] = useState<MobileTab>(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      let urlMint = params.get("mint") || params.get("ca") || params.get("token") || params.get("ticker");
      if (!urlMint) {
        const match = window.location.pathname.match(/^\/(?:token|ca|mint|app)\/([a-zA-Z0-9]{10,44})/i);
        if (match) urlMint = match[1];
      }
      if (urlMint && window.innerWidth < 768) {
        return "chart";
      }
      if (window.innerWidth < 768) {
        return "nursery";
      }
    }
    return "signals";
  });

  useEffect(() => {
    const fetchSol = () => getSolUsd().then(setSolPrice);
    fetchSol();
    const id = setInterval(fetchSol, 60_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    nursery.start();
    return () => nursery.stop();
  }, []);

  // Add token to history if it comes from URL share
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let urlMint = params.get("mint") || params.get("ca") || params.get("token") || params.get("ticker");
    if (!urlMint) {
      const match = window.location.pathname.match(/^\/(?:token|ca|mint|app)\/([a-zA-Z0-9]{10,44})/i);
      if (match) urlMint = match[1];
    }
    if (urlMint && urlMint.length >= 32 && looksLikeSolanaAddress(urlMint)) {
      setSearchHistory((prev) => {
        if (prev.some((p) => p.mint === urlMint)) return prev;
        
        fetchTokenMeta(urlMint).then((meta) => {
          if (meta) {
            addToSearchHistory({
              mint: urlMint,
              name: meta.name || "?",
              symbol: meta.symbol || "?",
              image: meta.imageUri || "",
              mcap: 0,
              priceUsd: 0,
              priceSol: 0,
              source: "Link",
            });
          }
        }).catch(() => {});
        return prev;
      });
    }
  }, [addToSearchHistory]);

  const setPreferredCurrency = useCallback((c: string) => {
    setPreferredCurrencyState(c);
    localStorage.setItem(PREFERRED_CURRENCY_KEY, c);
  }, []);

  const setCaMintInput = useCallback((v: string) => {
    if (v && v.toLowerCase() === ROGUE_MINT_BLACKLIST) {
      console.warn(`[AppContext] Blocked navigation to blacklisted rogue mint: ${v}`);
      return;
    }

    setCaMintInputState(v);
    if (v) {
      localStorage.setItem(CA_MINT_KEY, v);
    } else {
      localStorage.removeItem(CA_MINT_KEY);
    }
    
    try {
      const url = new URL(window.location.href);
      if (v) {
        url.searchParams.set("mint", v);
      } else {
        url.searchParams.delete("mint");
      }
      window.history.replaceState({}, "", url);
    } catch {
      // Ignore URL parsing errors
    }
  }, []);

  // Resolve tickers like $CrayonEater from URL to actual mint addresses
  useEffect(() => {
    if (caMintInput && !looksLikeSolanaAddress(caMintInput)) {
      const controller = new AbortController();
      (async () => {
        try {
          const res = await fetch(`/api/search?q=${encodeURIComponent(caMintInput)}`, { signal: controller.signal });
          if (res.ok && res.body) {
            const reader = res.body.getReader();
            const decoder = new TextDecoder("utf-8");
            let buffer = "";
            while (true) {
              const { value, done } = await reader.read();
              if (done) break;
              buffer += decoder.decode(value, { stream: true });
              const lines = buffer.split('\n');
              buffer = lines.pop() || "";
              let updated = false;
              for (const line of lines) {
                if (line.trim()) {
                  try {
                    const data = JSON.parse(line);
                    if (data.chunk && Array.isArray(data.chunk) && data.chunk.length > 0) {
                      const best = [...data.chunk].sort((a,b) => (b.mcap||0) - (a.mcap||0))[0];
                      if (best && best.mint && looksLikeSolanaAddress(best.mint)) {
                        setCaMintInput(best.mint);
                        addToSearchHistory(best);
                        updated = true;
                      }
                    }
                  } catch (e) {}
                }
              }
              if (updated) {
                controller.abort();
                break;
              }
            }
          }
        } catch (e) {}
      })();
      return () => controller.abort();
    }
  }, [caMintInput, setCaMintInput, addToSearchHistory]);

  useEffect(() => {
    fetch("/api/config")
      .then((res) => {
        if (res.ok) return res.json();
        throw new Error(`HTTP ${res.status}`);
      })
      .then((data) => {
        setBackendConfigState(data);
        setBackendConfig(data);
      })
      .catch((err) => {
        console.warn("[AppContext] Failed to load backend config:", err);
      });
  }, []);

  useEffect(() => {
    lsSave(THEME_STORAGE_KEY, theme);
    if (theme === "light") document.documentElement.classList.add("light");
    else document.documentElement.classList.remove("light");
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setThemeState((t) => (t === "light" ? "dark" : "light"));
  }, []);

  // Restore persisted handle on mount (requires one user permission click before use)
  useEffect(() => {
    if (!isFileSystemAccessSupported()) return;
    loadPersistedHandle().then((h) => {
      if (h) setLocalWorkspaceHandleState(h);
    }).catch(() => { /* storage unavailable */ });
  }, []);

  const connectLocalWorkspace = useCallback(async () => {
    const handle = await openLocalWorkspace();
    setLocalWorkspaceHandleState(handle);
  }, []);

  const disconnectLocalWorkspace = useCallback(() => {
    setLocalWorkspaceHandleState(null);
    setPendingLocalEdits(new Map());
    void clearPersistedHandle();
  }, []);

  const setModel = useCallback((patch: Partial<ModelSettings>) => {
    setModelState((prev) => {
      const next = { ...prev, ...patch };
      lsSave(MODEL_STORAGE_KEY, next);
      return next;
    });
  }, []);

  const setSessionModel = useCallback((id: string, patch: Partial<ModelSettings>) => {
    setSessionsState((prev) => ({
      ...prev,
      sessions: prev.sessions.map((s) => (s.id === id ? { ...s, modelOverride: { ...s.modelOverride, ...patch } } : s)),
    }));
  }, []);

  const clearSessionModel = useCallback((id: string) => {
    setSessionsState((prev) => ({
      ...prev,
      sessions: prev.sessions.map((s) => (s.id === id ? { ...s, modelOverride: undefined } : s)),
    }));
  }, []);

  const setActiveChatId = useCallback((id: string) => {
    setSessionsState((prev) => ({ ...prev, activeId: id }));
  }, []);

  const newChatSession = useCallback(() => {
    setSessionsState((prev) => {
      const s = makeGreetingSession();
      const next = [s, ...prev.sessions].slice(0, MAX_SESSIONS);
      return { sessions: next, activeId: s.id };
    });
  }, []);

  const closeChatSession = useCallback((id: string) => {
    setSessionsState((prev) => {
      const filtered = prev.sessions.filter((s) => s.id !== id);
      if (filtered.length === 0) {
        const s = makeGreetingSession();
        return { sessions: [s], activeId: s.id };
      }
      return {
        sessions: filtered,
        activeId: prev.activeId === id ? filtered[0].id : prev.activeId,
      };
    });
  }, []);

  const renameChatSession = useCallback((id: string, name: string) => {
    setSessionsState((prev) => ({
      ...prev,
      sessions: prev.sessions.map((s) => (s.id === id ? { ...s, name } : s)),
    }));
  }, []);

  const activeSession = chatSessions.find((s) => s.id === activeChatId) || chatSessions[0];
  const messages = activeSession.messages;

  const appendMessage = useCallback(
    (m: Omit<ChatMessage, "id" | "createdAt">) => {
      const id = crypto.randomUUID();
      const msg = { ...m, id, createdAt: Date.now() };
      setSessionsState((prev) => ({
        ...prev,
        sessions: prev.sessions.map((s) =>
          s.id === activeChatId ? { ...s, messages: [...s.messages, msg].slice(-MAX_STORED_MESSAGES) } : s,
        ),
      }));
      return id;
    },
    [activeChatId],
  );

  const updateMessage = useCallback(
    (id: string, patch: Partial<Pick<ChatMessage, "content">>) => {
      setSessionsState((prev) => ({
        ...prev,
        sessions: prev.sessions.map((s) =>
          s.id === activeChatId
            ? { ...s, messages: s.messages.map((m) => (m.id === id ? { ...m, ...patch } : m)) }
            : s,
        ),
      }));
    },
    [activeChatId],
  );

  const clearChat = useCallback(() => {
    setSessionsState((prev) => ({
      ...prev,
      sessions: prev.sessions.map((s) => (s.id === activeChatId ? { ...s, messages: [] } : s)),
    }));
  }, [activeChatId]);

  const openSetupPanel = useCallback(() => {
    setSidebarMode("models");
    setSidebarOpen(true);
    setActivitySection("models");
  }, []);

  const navigateChartToMint = useCallback((mint: string) => {
    setCaMintInput(mint);
    setActivitySection("analytics");
    setSidebarMode("analytics");
    setSidebarOpen(true);
  }, []);

  const setChartAnalytics = useCallback((patch: Partial<ChartAnalyticsState>) => {
    setChartAnalyticsState((prev) => ({ ...prev, ...patch }));
  }, []);

  const addUserAlgo = useCallback((name: string, description: string) => {
    setUserAlgos((prev) => {
      const next = [{ id: crypto.randomUUID(), name, description, createdAt: Date.now() }, ...prev];
      lsSave(USER_ALGOS_KEY, next);
      return next;
    });
  }, []);

  const removeUserAlgo = useCallback((id: string) => {
    setUserAlgos((prev) => {
      const next = prev.filter((a) => a.id !== id);
      lsSave(USER_ALGOS_KEY, next);
      return next;
    });
  }, []);

  const setSelectedAlgoId = useCallback((id: string | null) => {
    setSelectedAlgoIdState(id);
    if (id) localStorage.setItem(SELECTED_ALGO_KEY, id);
    else localStorage.removeItem(SELECTED_ALGO_KEY);
  }, []);


  const setScalperLiveBuySol = useCallback((v: number) => {
    const clamped = clampScalperLiveBuySol(v);
    setScalperLiveBuySolState(clamped);
    localStorage.setItem(SCALPER_LIVE_BUY_SOL_KEY, String(clamped));
  }, []);

  const setTradingMode = useCallback((m: TradingMode) => {
    setTradingModeState(m);
    localStorage.setItem(TRADING_MODE_STORAGE_KEY, m);
  }, []);

  const hardStopTrading = useCallback(() => {
    setAlgoSessionActive(false);
    setTradingHalted(true);
  }, []);

  const requestManualSell = useCallback(() => setManualSellRequested(true), []);
  const clearManualSellRequest = useCallback(() => setManualSellRequested(false), []);

  const setScalperUserConfig = useCallback((patch: Partial<ScalperUserConfig>) => {
    setScalperUserConfigState((prev) => {
      const next = { ...prev, ...patch };
      lsSave(SCALPER_USER_CONFIG_KEY, next);
      
      // Sync with server bot runner
      fetch("/api/bot/config", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(next)
      }).catch(e => console.error("[Sync Config] Error syncing to server:", e));

      return next;
    });
  }, []);

  const setStrategistThresholds = useCallback((patch: Partial<StrategistThresholds>) => {
    setStrategistThresholdsState((prev) => {
      const next = { ...prev, ...patch };
      lsSave(STRATEGIST_THRESHOLDS_KEY, next);
      return next;
    });
  }, []);

  const setLayoutMode = useCallback((m: LayoutMode) => {
    setLayoutModeState(m);
    localStorage.setItem(LAYOUT_MODE_KEY, m);
  }, []);

  const toggleFavorite = useCallback((mint: string) => {
    setFavorites((prev) => {
      const next = prev.includes(mint) ? prev.filter((m) => m !== mint) : [mint, ...prev];
      lsSave(FAVORITES_KEY, next);
      return next;
    });
  }, []);

  const setHeliosKey = useCallback((key: string) => {
    setHeliosKeyState(key);
    try {
      if (key.trim()) localStorage.setItem(HELIOS_KEY, key.trim());
      else localStorage.removeItem(HELIOS_KEY);
    } catch { /* ignore */ }
  }, []);

  const setMobileActiveTab = useCallback((tab: MobileTab) => {
    setMobileActiveTabState(tab);
  }, []);

  const setDetectedZones = useCallback((mint: string, zones: BounceZone[]) => {
    setBounceZones((prev) => {
      const manualForMint = prev.filter((z) => z.mint === mint && z.touches === 0);
      const others = prev.filter((z) => z.mint !== mint);
      const mapped: UserBounceZone[] = zones.map((z) => ({
        id: crypto.randomUUID(),
        mint,
        price: z.price,
        touches: z.touches,
        enabled: true,
        strength: z.strength,
        sources: z.sources,
      }));
      const next = [...others, ...manualForMint, ...mapped];
      lsSave(BOUNCE_ZONES_KEY, next);
      return next;
    });
  }, []);

  const [bounceSuggestionTick, setBounceSuggestionTick] = useState(0);
  const refreshSuggestedBounceZones = useCallback(() => {
    setBounceSuggestionTick((t) => t + 1);
  }, []);

  const [floorCandlesStatus, setFloorCandlesStatus] = useState<"idle" | "loading" | "ready">("idle");

  const toggleBounceZone = useCallback((id: string) => {
    setBounceZones((prev) => {
      const next = prev.map((z) => (z.id === id ? { ...z, enabled: !z.enabled } : z));
      lsSave(BOUNCE_ZONES_KEY, next);
      return next;
    });
  }, []);

  const updateBounceZonePrice = useCallback((id: string, price: number) => {
    setBounceZones((prev) => {
      const next = prev.map((z) => (z.id === id ? { ...z, price } : z));
      lsSave(BOUNCE_ZONES_KEY, next);
      return next;
    });
  }, []);

  const addBounceZone = useCallback((mint: string, price: number) => {
    setBounceZones((prev) => {
      const next = [
        ...prev,
        {
          id: crypto.randomUUID(),
          mint,
          price,
          touches: 0,
          enabled: true,
          strength: 0,
        },
      ];
      lsSave(BOUNCE_ZONES_KEY, next);
      return next;
    });
  }, []);

  const removeBounceZone = useCallback((id: string) => {
    setBounceZones((prev) => {
      const next = prev.filter((z) => z.id !== id);
      lsSave(BOUNCE_ZONES_KEY, next);
      return next;
    });
  }, []);

  const appendPersistedTrades = useCallback((trades: PersistedBotTrade[]) => {
    setPersistedBotTrades((prev) => {
      const existingIds = new Set(prev.map((t) => t.id));
      const fresh = trades.filter((t) => !existingIds.has(t.id));
      if (fresh.length === 0) return prev;
      const next = [...fresh, ...prev].slice(0, MAX_PERSISTED_TRADES);
      lsSave(PERSISTED_TRADES_KEY, next);
      return next;
    });
  }, []);

  const clearPersistedTrades = useCallback(() => {
    setPersistedBotTrades([]);
    localStorage.removeItem(PERSISTED_TRADES_KEY);
  }, []);

  const setGithubWorkspace = useCallback((patch: Partial<GitHubWorkspaceSettings>) => {
    setGithubWorkspaceState((prev) => {
      const next = { ...prev, ...patch };
      lsSave(GITHUB_WORKSPACE_KEY, next);
      return next;
    });
  }, []);

  const setOpenFile = useCallback((path: string | null, content: string | null) => {
    setOpenFilePath(path);
    setOpenFileContent(content);
  }, []);

  const applyFileEdit = useCallback(
    async (path: string, code: string, commitMessage = "Apply edit from chat") => {
      if (localWorkspaceHandle) {
        await writeLocalFile(localWorkspaceHandle, path, code);
        setPendingLocalEdits((prev) => {
          const next = new Map(prev);
          next.set(path, code);
          return next;
        });
        setApplyEditTick((t) => t + 1);
        setLastAppliedPath(path);
        return "";
      }

      const result = await githubPutFileContent({
        ...githubWorkspace,
        path,
        contentUtf8: code,
        message: commitMessage,
      });
      setApplyEditTick((t) => t + 1);
      setLastAppliedPath(path);
      return typeof result === "string" ? result : result.commitSha;
    },
    [localWorkspaceHandle, githubWorkspace],
  );

  const pushPendingToGitHub = useCallback(async () => {
    if (pendingLocalEdits.size === 0) return;
    const paths = Array.from(pendingLocalEdits.keys());
    const msg = `Push ${paths.length} local edits: ${paths.join(", ")}`;

    for (const [path, code] of pendingLocalEdits) {
      await githubPutFileContent({
        ...githubWorkspace,
        path,
        contentUtf8: code,
        message: msg,
      });
    }
    setPendingLocalEdits(new Map());
  }, [pendingLocalEdits, githubWorkspace]);

  useEffect(() => {
    nursery.setLaunchProviderFilter(launchProviderFilter);
  }, [launchProviderFilter]);

  const value = useMemo(
    () => ({
      model, setModel,
      backendConfig,
      launchProviderFilter, setLaunchProviderFilter,
      isDeployModalOpen, openDeployModal: () => setIsDeployModalOpen(true), closeDeployModal: () => setIsDeployModalOpen(false),
      chatSessions, activeChatId, setActiveChatId,
      newChatSession, closeChatSession, renameChatSession,
      setSessionModel, clearSessionModel,
      messages, appendMessage, updateMessage, clearChat,
      openSetupPanel, navigateChartToMint,
      chartAnalytics, setChartAnalytics,
      composerBusy, setComposerBusy,
      sidebarMode, setSidebarMode,
      sidebarOpen, setSidebarOpen,
      activitySection, setActivitySection,
      userAlgos, addUserAlgo, removeUserAlgo,
      selectedAlgoId, setSelectedAlgoId,
      caMintInput, setCaMintInput,
      preferredCurrency, setPreferredCurrency,
      scalperLiveBuySol, setScalperLiveBuySol,
      tradingMode, setTradingMode,
      algoSessionActive, setAlgoSessionActive,
      tradingHalted, setTradingHalted,
      hardStopTrading,
      manualSellRequested, requestManualSell, clearManualSellRequest,
      scalperUserConfig, setScalperUserConfig,
      strategistMode, setStrategistMode,
      autoPilotEnabled, setAutoPilotEnabled,
      autoScrollEnabled, setAutoScrollEnabled,
      layoutMode, setLayoutMode,
      isFocusChartVisible, setIsFocusChartVisible,
      bounceZones, setDetectedZones,
      bounceSuggestionTick, refreshSuggestedBounceZones,
      floorCandlesStatus, setFloorCandlesStatus,
      toggleBounceZone, updateBounceZonePrice, addBounceZone, removeBounceZone,
      persistedBotTrades, appendPersistedTrades, clearPersistedTrades,
      githubWorkspace, setGithubWorkspace,
      openFilePath, openFileContent, setOpenFile,
      workspaceFilePaths, setWorkspaceFilePaths,
      applyEditTick, lastAppliedPath, applyFileEdit,
      localWorkspaceHandle, connectLocalWorkspace, disconnectLocalWorkspace,
      pendingLocalEdits, pushPendingToGitHub,
      theme, setTheme: setThemeState, toggleTheme,
      strategistThresholds, setStrategistThresholds,
      favorites, toggleFavorite,
      isTrading, setIsTrading,
      solPrice,
      heliosKey, setHeliosKey,
      mobileActiveTab, setMobileActiveTab,
      locale, setLocale,
      searchHistory, addToSearchHistory, removeFromSearchHistory,
      globalAirdropLists, setGlobalAirdropLists,
      appToasts, addAppToast, removeAppToast,
      presavedGraphs, setPresavedGraphs, savePresavedGraph,
      twitterUser, loginWithTwitter, logoutTwitter,
    }),
    [
      model, setModel,
      backendConfig,
      launchProviderFilter, setLaunchProviderFilter,
      isDeployModalOpen,
      chatSessions, activeChatId, setActiveChatId,
      newChatSession, closeChatSession, renameChatSession,
      setSessionModel, clearSessionModel,
      messages, appendMessage, updateMessage, clearChat,
      openSetupPanel, navigateChartToMint,
      chartAnalytics, setChartAnalytics,
      composerBusy, setComposerBusy,
      sidebarMode, setSidebarMode,
      sidebarOpen, setSidebarOpen,
      activitySection, setActivitySection,
      userAlgos, addUserAlgo, removeUserAlgo,
      selectedAlgoId, setSelectedAlgoId,
      caMintInput, setCaMintInput,
      preferredCurrency, setPreferredCurrency,
      scalperLiveBuySol, setScalperLiveBuySol,
      tradingMode, setTradingMode,
      algoSessionActive, setAlgoSessionActive,
      tradingHalted, setTradingHalted,
      hardStopTrading,
      manualSellRequested, requestManualSell, clearManualSellRequest,
      scalperUserConfig, setScalperUserConfig,
      strategistMode, setStrategistMode,
      autoPilotEnabled, setAutoPilotEnabled,
      autoScrollEnabled, setAutoScrollEnabled,
      layoutMode, setLayoutMode,
      isFocusChartVisible, setIsFocusChartVisible,
      bounceZones, setDetectedZones,
      bounceSuggestionTick, refreshSuggestedBounceZones,
      floorCandlesStatus, setFloorCandlesStatus,
      toggleBounceZone, updateBounceZonePrice, addBounceZone, removeBounceZone,
      persistedBotTrades, appendPersistedTrades, clearPersistedTrades,
      githubWorkspace, setGithubWorkspace,
      openFilePath, openFileContent, setOpenFile,
      workspaceFilePaths, setWorkspaceFilePaths,
      applyEditTick, lastAppliedPath, applyFileEdit,
      localWorkspaceHandle, connectLocalWorkspace, disconnectLocalWorkspace,
      pendingLocalEdits, pushPendingToGitHub,
      theme, toggleTheme,
      strategistThresholds, setStrategistThresholds,
      favorites, toggleFavorite,
      isTrading, setIsTrading,
      solPrice,
      heliosKey, setHeliosKey,
      mobileActiveTab, setMobileActiveTab,
      locale, setLocale,
      searchHistory, addToSearchHistory, removeFromSearchHistory,
      globalAirdropLists, setGlobalAirdropLists,
      appToasts, addAppToast, removeAppToast,
      presavedGraphs, setPresavedGraphs, savePresavedGraph,
      twitterUser, loginWithTwitter, logoutTwitter,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
